import {
  Banknote,
  Briefcase,
  Building2,
  ClipboardCheck,
  ClipboardList,
  Contact,
  Factory,
  FileArchive,
  FileCheck,
  FileText,
  Filter,
  FolderKanban,
  Landmark,
  Layers,
  Lock,
  MapPin,
  MessageSquarePlus,
  Package,
  PackageCheck,
  PackageSearch,
  PencilRuler,
  Search,
  ShieldAlert,
  ShoppingCart,
  Tags,
  TrendingUp,
  Truck,
  UserCog,
  UserRound,
  Wrench,
} from 'lucide-react';
import type {
  AssemblyReportStatus,
  AssemblyTaskStatus,
  FeedbackStatus,
  GoodsReceiptStatus,
  InventoryCountStatus,
  PickingStatus,
  ProductionOrderStatus,
  ProjectStatus,
  PurchaseOrderStatus,
  QmProcessStatus,
  SalesDocumentKind,
  SearchGroup,
  SearchHit,
  SearchResourceType,
  TransportStatus,
} from '@proerp/shared';
import { FEEDBACK_STATUS_LABELS, QM_STATUS_LABELS, SEARCH_RESOURCE_LABELS } from '@proerp/shared';
import { TASK_STATUS } from '@/features/assembly/assemblyMeta';
import { PICKING_STATUS_META } from '@/features/commissioning/commissioningMeta';
import { goodsReceiptStatusLabel } from '@/features/goods-receipt/goodsReceiptMeta';
import { purchaseStatusLabel } from '@/features/procurement/procurementMeta';
import { projectStatusLabel } from '@/features/projects/statusBadge';
import { kindLabel } from '@/features/sales/salesMeta';
import { TRANSPORT_STATUS_META } from '@/features/transport/transportMeta';

/**
 * Ressourcentypen der Ausbaustufe „wirklich alles durchsuchen" (Tech-Lead
 * 2026-08-12).
 *
 * Sie stehen hier als eigene Liste, damit die Oberfläche vollständig gebaut
 * werden kann, während `packages/shared` (Backend-Seite) noch nachzieht: Sobald
 * die Typen dort ankommen, fällt dieser Teil der Vereinigung in
 * `SearchResourceType` zusammen und ändert nichts. Streicht das Backend einen
 * Typ wieder (weil das Datenmodell ihn nicht hergibt), bleibt hier ein
 * unbenutzter Eintrag stehen — harmlos, aber dann bitte aufräumen.
 *
 * Wichtig: Die Vollständigkeit der Zuordnungen (Icon, Label, Route) erzwingt
 * TypeScript weiterhin über `Record<SearchResourceTypeUi, …>` — ein neuer Typ
 * ohne Ziel-Route bricht den Typecheck.
 */
export const EXTRA_SEARCH_RESOURCE_TYPES = [
  'delivery-address',
  'transport',
  'blocked-stock',
  'qm-process',
  'material-list',
  'forecast',
  'goods-receipt',
  'picking-order',
  'drawing-number',
  'material-code',
  'feedback',
  'sales-pipeline',
  'inventory-count',
  'assembly-report',
  'legal-entity',
  'user',
] as const;

type ExtraSearchResourceType = (typeof EXTRA_SEARCH_RESOURCE_TYPES)[number];

/** Ressourcentyp, wie ihn die Oberfläche kennt (geteilte + noch nachziehende Typen). */
export type SearchResourceTypeUi = SearchResourceType | ExtraSearchResourceType;

/** Icon je Ressourcentyp — geteilt von Command-Palette und Ergebnis-Seite. */
export const RESOURCE_ICON: Record<SearchResourceTypeUi, typeof Search> = {
  project: FolderKanban,
  customer: Building2,
  article: Package,
  contact: Contact,
  'commodity-group': Tags,
  'sales-document': FileText,
  'purchase-order': ShoppingCart,
  'production-order': Factory,
  'assembly-task': Wrench,
  maintenance: Wrench,
  payment: Banknote,
  employee: UserRound,
  position: Briefcase,
  'dms-document': FileArchive,
  'delivery-address': MapPin,
  transport: Truck,
  'blocked-stock': Lock,
  'qm-process': ShieldAlert,
  'material-list': ClipboardList,
  forecast: TrendingUp,
  'goods-receipt': PackageCheck,
  'picking-order': PackageSearch,
  'drawing-number': PencilRuler,
  'material-code': Layers,
  feedback: MessageSquarePlus,
  'sales-pipeline': Filter,
  'inventory-count': ClipboardCheck,
  'assembly-report': FileCheck,
  'legal-entity': Landmark,
  user: UserCog,
};

/**
 * Deutsche Labels der noch nicht geteilten Typen. Sobald `packages/shared` die
 * Typen führt, gewinnt `SEARCH_RESOURCE_LABELS` (steht im Spread hinten) — die
 * Labels laufen also nicht auseinander.
 */
const EXTRA_RESOURCE_LABELS: Record<ExtraSearchResourceType, string> = {
  'delivery-address': 'Lieferadressen',
  transport: 'Transporte',
  'blocked-stock': 'Sperrlager',
  'qm-process': 'QM-Prozesse',
  'material-list': 'Bedarfslisten',
  forecast: 'Bedarfsvorschauen',
  'goods-receipt': 'Wareneingänge',
  'picking-order': 'Kommissionieraufträge',
  'drawing-number': 'Zeichnungsnummern',
  'material-code': 'Materialcodes',
  feedback: 'Verbesserungen',
  'sales-pipeline': 'Vertriebspipeline',
  'inventory-count': 'Inventuren',
  'assembly-report': 'Baustellenberichte',
  'legal-entity': 'Gesellschaften',
  user: 'Benutzerkonten',
};

/** Gruppen-Label je Ressourcentyp (Rückfall, wenn die Antwort keins mitbringt). */
export const RESOURCE_LABEL: Record<SearchResourceTypeUi, string> = {
  ...EXTRA_RESOURCE_LABELS,
  ...SEARCH_RESOURCE_LABELS,
};

/* ─── Trefferqualität: genau vs. ähnlich ─────────────────────────────────── */

/**
 * Wie ein Treffer zustande kam (Vertrag mit dem Backend):
 * - `identifier` — Kennung/Nummer stimmt (P-1001, 1234.56789.012)
 * - `contains`   — der Suchbegriff steckt im Text
 * - `similar`    — nur ÄHNLICH geschrieben (Tippfehler, andere Schreibweise)
 */
export const SEARCH_MATCH_KINDS = ['identifier', 'contains', 'similar'] as const;
export type SearchMatchKind = (typeof SEARCH_MATCH_KINDS)[number];

/**
 * Treffer mit den Feldern des erweiterten Vertrags. Beide Felder sind hier
 * optional beschrieben, damit die Oberfläche sowohl gegen den alten als auch
 * gegen den neuen `SearchHit` aus `packages/shared` übersetzt — und damit eine
 * ältere API-Antwort (ohne die Felder) die Palette nicht umwirft.
 */
type MatchAwareHit = SearchHit & { match?: SearchMatchKind; score?: number | null };

/** Trefferart eines Treffers; ohne Angabe gilt er als genauer Treffer. */
export function hitMatchKind(hit: SearchHit): SearchMatchKind {
  return (hit as MatchAwareHit).match ?? 'contains';
}

/** Nur ähnlich geschrieben — gehört in den eigenen Abschnitt „Ähnliche Treffer". */
export function isSimilarHit(hit: SearchHit): boolean {
  return hitMatchKind(hit) === 'similar';
}

/** Ähnlichkeit 0..1 (nur bei `match: 'similar'` gesetzt), sonst `null`. */
export function hitScore(hit: SearchHit): number | null {
  const score = (hit as MatchAwareHit).score;
  return typeof score === 'number' ? score : null;
}

/** Ähnlichkeit als Prozenttext („78 %"); `null`, wenn das Backend keinen Wert liefert. */
export function formatSimilarity(hit: SearchHit): string | null {
  const score = hitScore(hit);
  if (score === null) return null;
  const clamped = Math.min(1, Math.max(0, score));
  return `${Math.round(clamped * 100)} %`;
}

/** Ein flach gelisteter „ähnlicher" Treffer samt seinem Gruppen-Label. */
export interface SimilarHit {
  hit: SearchHit;
  label: string;
}

/** Ergebnis-Aufteilung: genaue Treffer bleiben gruppiert, ähnliche werden flach. */
export interface SplitSearchResult {
  /** Genaue Treffer (Kennung/Text), weiterhin nach Typ gruppiert. */
  exactGroups: SearchGroup[];
  /** Ähnliche Treffer aller Typen, flach und nach Ähnlichkeit absteigend. */
  similarHits: SimilarHit[];
}

/**
 * Rang einer Gruppe für die Reihenfolge: Gruppen mit einem Kennungs-Treffer
 * zuerst. Alles andere behält die Reihenfolge der Antwort (fest über
 * `SEARCH_RESOURCE_TYPES`) — `Array.sort` ist stabil.
 *
 * Hintergrund: Mit über zwei Dutzend Typen steht die gesuchte Nummer sonst weit
 * unten, nur weil ihr Typ in der festen Liste hinten steht.
 */
function groupRank(group: SearchGroup): number {
  return group.hits.some((hit) => hitMatchKind(hit) === 'identifier') ? 0 : 1;
}

/**
 * Trennt genaue von ähnlichen Treffern und sortiert die Gruppen (Kennung zuerst).
 *
 * Ähnliche Treffer werden bewusst **flach** gelistet statt je Typ gruppiert: sie
 * sind eine Rückfrage („meintest du …?"), keine vollständige Ergebnisliste — und
 * ein Dutzend Ein-Treffer-Gruppen wäre nur Rauschen. Sortiert wird nach
 * Ähnlichkeit, damit die beste Vermutung oben steht.
 */
export function splitSearchGroups(groups: SearchGroup[]): SplitSearchResult {
  const exactGroups: SearchGroup[] = [];
  const similarHits: SimilarHit[] = [];

  for (const group of groups) {
    const label = group.label || RESOURCE_LABEL[group.type];
    const exact: SearchHit[] = [];
    for (const hit of group.hits) {
      if (isSimilarHit(hit)) similarHits.push({ hit, label });
      else exact.push(hit);
    }
    if (exact.length > 0) exactGroups.push({ ...group, hits: exact });
  }

  exactGroups.sort((a, b) => groupRank(a) - groupRank(b));
  similarHits.sort((a, b) => (hitScore(b.hit) ?? 0) - (hitScore(a.hit) ?? 0));
  return { exactGroups, similarHits };
}

/** Summe der Treffer über alle Gruppen. */
export function countHits(groups: SearchGroup[]): number {
  return groups.reduce((sum, group) => sum + group.hits.length, 0);
}

/* ─── Badge: Code des Servers → Wort der Oberfläche ──────────────────────── */

/** Status ohne eigenen Label-Helfer im Frontend — Wörter wie in den jeweiligen Seiten. */
const INVENTORY_COUNT_STATUS_LABELS: Record<InventoryCountStatus, string> = {
  ENTWURF: 'Entwurf',
  ABGESCHLOSSEN: 'Abgeschlossen',
};
const PRODUCTION_ORDER_STATUS_LABELS: Record<ProductionOrderStatus, string> = {
  PLANNED: 'Geplant',
  RELEASED: 'Freigegeben',
  IN_PROGRESS: 'In Arbeit',
  DONE: 'Fertiggemeldet',
  CANCELLED: 'Storniert',
};
const ASSEMBLY_REPORT_STATUS_LABELS: Record<AssemblyReportStatus, string> = {
  ENTWURF: 'Entwurf',
  FERTIG: 'Fertiggestellt',
};

/** Übersetzer je Treffertyp; `code` ist der rohe Wert aus `hit.badge`. */
const BADGE_LABEL_BY_TYPE: Partial<Record<SearchResourceTypeUi, (code: string) => string | undefined>> = {
  project: (code) => lookup(code, (status: ProjectStatus) => projectStatusLabel(status)),
  'sales-document': (code) => lookup(code, (kind: SalesDocumentKind) => kindLabel(kind)),
  // Ohne Belegart im Treffer: die belegart-neutrale Form („Bestellt" statt „Angefragt").
  'purchase-order': (code) => lookup(code, (status: PurchaseOrderStatus) => purchaseStatusLabel(status)),
  'goods-receipt': (code) => lookup(code, (status: GoodsReceiptStatus) => goodsReceiptStatusLabel(status)),
  'picking-order': (code) => lookup(code, (status: PickingStatus) => PICKING_STATUS_META[status].label),
  // Ohne Richtung im Treffer: das richtungsneutrale Wort („Abgefahren", nicht „Angekommen").
  transport: (code) => lookup(code, (status: TransportStatus) => TRANSPORT_STATUS_META[status].label),
  'inventory-count': (code) => lookup(code, (status: InventoryCountStatus) => INVENTORY_COUNT_STATUS_LABELS[status]),
  'production-order': (code) => lookup(code, (status: ProductionOrderStatus) => PRODUCTION_ORDER_STATUS_LABELS[status]),
  'assembly-task': (code) => lookup(code, (status: AssemblyTaskStatus) => TASK_STATUS[status].label),
  'assembly-report': (code) => lookup(code, (status: AssemblyReportStatus) => ASSEMBLY_REPORT_STATUS_LABELS[status]),
  'qm-process': (code) => lookup(code, (status: QmProcessStatus) => QM_STATUS_LABELS[status]),
  feedback: (code) => lookup(code, (status: FeedbackStatus) => FEEDBACK_STATUS_LABELS[status]),
};

/**
 * Nachschlagen mit Absicherung: Ein Code, den die Tabelle (noch) nicht kennt,
 * liefert `undefined` statt eines Absturzes (`TASK_STATUS[x].label` auf undefined).
 */
function lookup<T extends string>(code: string, label: (value: T) => string | undefined): string | undefined {
  try {
    return label(code as T) || undefined;
  } catch {
    return undefined;
  }
}

/**
 * Badge-Text eines Treffers auf Deutsch (Review-Fund 2026-09-28): Die Suche liefert
 * Status und Belegart als Code („QUOTE", „ORDERED", „GEPRUEFT") — gezeigt werden
 * dieselben Wörter wie in den Listen der Module. Unbekanntes bleibt, wie es ist.
 */
export function searchBadgeLabel(hit: Pick<SearchHit, 'type' | 'badge'>): string | null {
  if (!hit.badge) return null;
  if (hit.badge === 'INAKTIV') return 'Inaktiv';
  return BADGE_LABEL_BY_TYPE[hit.type as SearchResourceTypeUi]?.(hit.badge) ?? hit.badge;
}
